import React from 'react';
import classnames from 'classnames';
import { FaCheckCircle, FaFileContract } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import RichTextDescriptionItem from 'Core/components/RichTextDescriptionItem';
import { PrimaryButton, OutlineButton } from 'Core/components/Button';
import { formatCurrency } from 'Core/utils/numberFormat';

const formatPercent = (value, fractionDigits = 2) => {
  if (value === null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(fractionDigits)}%`;
};

const formatMoney = (value) => {
  if (value === null || Number.isNaN(Number(value))) return '—';
  return formatCurrency(value, { withDecimals: true });
};

const AgreementSection = ({ title, children }) => (
  <section className="py-3 border-t border-slate-200 first:border-t-0 first:pt-0">
    <h3 className="!mt-0 !mb-2 text-xs font-bold uppercase tracking-[0.18em] text-gray-500">
      {title}
    </h3>
    <dl className="divide-y divide-slate-100">{children}</dl>
  </section>
);

const AgreementRow = ({ label, value, sublabel, emphasize = false, testId }) => (
  <div className="flex justify-between items-start py-1.5 gap-4">
    <dt className="text-sm text-gray-700 max-w-[60%] leading-snug">
      {label}
      {sublabel && <div className="text-xs text-gray-400 mt-0.5 leading-snug">{sublabel}</div>}
    </dt>
    <dd
      className={classnames(
        'text-right whitespace-nowrap',
        emphasize ? 'text-sm font-bold text-gray-900' : 'text-sm font-semibold text-gray-800'
      )}
      data-testid={testId}
    >
      {value}
    </dd>
  </div>
);

export default function OfferAgreement({
  installment,
  amount_to_pay,
  already_paid_amount,
  downPayment = 0,
  approval,
  isSelected,
  onSelectOffer,
  onBack,
  canSelect,
}) {
  const { t } = useTranslation();
  const {
    program = 'Fortiva',
    type = '',
    rich_text_descriptions: richTextDescriptions,
    terms = [],
  } = installment || {};

  const term = terms[0] || {};
  const { id, apr, waived_interest: waivedInterest, fees } = term;

  const { annual_fee = 0 } = fees || {};

  const { days: waivedDays = 0, text: waivedText = '' } = waivedInterest || {};
  const hasWaivedInterest = Number(waivedDays) > 0 || Boolean(waivedText);

  const downPaymentValue = Number(downPayment) || 0;
  const financedAmount = Math.max(amount_to_pay - already_paid_amount - downPaymentValue, 0);
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="w-full max-w-3xl mx-auto mt-3 mb-2">
      <article
        className={classnames(
          'bg-white rounded-lg border shadow-md overflow-hidden',
          isSelected ? 'border-fortiva-bg ring-1 ring-fortiva-bg' : 'border-slate-300'
        )}
      >
        <header className="bg-slate-50 border-b border-slate-200 px-6 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center text-gray-500 text-xs uppercase tracking-[0.2em] font-semibold">
                <FaFileContract className="mr-2" />
                Credit Agreement
              </div>
              <h2
                className="!mt-0.5 !mb-0.5 text-xl font-serif font-bold text-gray-900"
                data-testid="agreement-title"
              >
                {program} Personal Credit Account
              </h2>
              <p className="!mb-0 text-xs text-gray-500 leading-snug">
                Congratulations, you&apos;re prequalified for a Fortiva Retail Credit revolving
                credit card issued by The Bank of Missouri, Perryville, MO. You should have received
                an offer email that summarizes the terms and conditions of your account and includes
                a unique offer code.
              </p>
            </div>
            <div className="text-right text-xs text-gray-500 shrink-0 leading-snug">
              <div>
                <span className="uppercase tracking-wider font-semibold text-gray-400">
                  Reference
                </span>
                <div className="text-gray-700 font-mono" data-testid="agreement-ref">
                  #{id || '—'}
                </div>
              </div>
              <div className="mt-1">
                <span className="uppercase tracking-wider font-semibold text-gray-400">Issued</span>
                <div className="text-gray-700">{today}</div>
              </div>
            </div>
          </div>
        </header>

        <div className="px-6 py-1">
          <AgreementSection title="Account Summary">
            <AgreementRow label="Lender" value={program} />
            <AgreementRow label="Account Type" value={type || '—'} testId="agreement-type" />
            <AgreementRow
              label="Approved Credit Limit"
              value={formatMoney(approval)}
              emphasize
              testId="agreement-credit-limit"
            />
          </AgreementSection>

          <AgreementSection title="Interest Rates and Charges">
            <AgreementRow
              label="Annual Percentage Rate (APR)"
              sublabel="Applies to all balances unless a promotional rate is in effect."
              value={formatPercent(apr)}
              emphasize
              testId="agreement-apr"
            />
          </AgreementSection>

          <AgreementSection title="Fee Schedule">
            <AgreementRow label="Annual Fee" value={formatMoney(annual_fee)} />
          </AgreementSection>

          {hasWaivedInterest && (
            <AgreementSection title="Promotional Terms">
              <AgreementRow
                label="Waived Interest Period"
                value={waivedDays ? `${waivedDays} days` : '—'}
              />
              {waivedText && (
                <div className="py-1.5 text-xs text-gray-600 italic leading-snug">{waivedText}</div>
              )}
            </AgreementSection>
          )}

          <AgreementSection title="Transaction Summary">
            <AgreementRow
              label="Total Purchase Amount"
              value={formatMoney(amount_to_pay)}
              testId="agreement-purchase-amount"
            />
            <AgreementRow label="Already Paid" value={formatMoney(already_paid_amount)} />
            <AgreementRow
              label="Down Payment"
              value={formatMoney(downPaymentValue)}
              testId="agreement-down-payment"
            />
            <AgreementRow
              label="Amount to be Financed"
              value={formatMoney(financedAmount)}
              emphasize
              testId="agreement-financed-amount"
            />
          </AgreementSection>

          {Array.isArray(richTextDescriptions) && richTextDescriptions.length > 0 && (
            <AgreementSection title="Additional Disclosures">
              <ul className="pt-1">
                {richTextDescriptions.map((item) => (
                  <RichTextDescriptionItem
                    key={item.id}
                    id={item.id}
                    iconName={item.icon_name}
                    title={item.title}
                    text={item.text}
                  />
                ))}
              </ul>
            </AgreementSection>
          )}
        </div>

        <footer className="bg-slate-50 border-t border-slate-200 px-6 py-4">
          <div className="text-xs text-gray-500 leading-snug mb-3">
            By selecting this offer, you acknowledge that you have reviewed the terms listed above,
            including the Annual Percentage Rate, fee schedule, and repayment obligations, and you
            wish to proceed with this {program} credit account. Final acceptance requires signing
            the full disclosure in the next step.
          </div>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center text-sm text-gray-600">
              {isSelected ? (
                <>
                  <FaCheckCircle className="text-green-500 mr-2" />
                  <span className="font-medium">Offer selected. Continue to the next step.</span>
                </>
              ) : (
                <span>Review the terms above, then select the offer to continue.</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {onBack && (
                <OutlineButton
                  data-testid="btn-select-offer-back"
                  className="w-32"
                  onClick={onBack}
                >
                  {t('fortiva.back')}
                </OutlineButton>
              )}
              <PrimaryButton
                data-testid="btn-select-offer"
                disabled={!canSelect}
                className="w-48"
                onClick={onSelectOffer}
              >
                {t('fortiva.continue')}
              </PrimaryButton>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
}
